import React, { useState, useEffect, useCallback } from 'react';
import { SimpleModulePage } from '../../modules/SimpleModulePage';
import BodyMeasurementModal from '../../components/BodyMeasurementModal';
import BodyMeasurementSyncModal from '../../components/BodyMeasurementSyncModal';
import { getBodyMeasurements, deleteBodyMeasurement } from '../../services/bodyMeasurementsService';
import { config } from '../../config/environment';
import type { BodyMeasurement } from '../../types/bodyMeasurements';

const SectionCard: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = "" }) => (
  <div className={`mx-4 mt-3 rounded-2xl bg-white shadow-sm px-4 py-3 mb-3 border border-slate-100 ${className}`}>{children}</div>
);

interface BodyMeasurementsPageProps {
  onBack: () => void;
}

export const BodyMeasurementsPage: React.FC<BodyMeasurementsPageProps> = ({ onBack }) => {
  const [measurements, setMeasurements] = useState<BodyMeasurement[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedMeasurement, setSelectedMeasurement] = useState<BodyMeasurement | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [profile, setProfile] = useState<{ height?: number | null } | null>(null);

  const loadMeasurements = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getBodyMeasurements(1, 10);
      if (response.success && response.data) {
        setMeasurements(response.data);
      }
      
      // 프로필 정보 로드 (height 필드 포함)
      const token = localStorage.getItem('authToken');
      if (token) {
        const profileResponse = await fetch(`${config.apiUrl}/user/profile`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        
        if (profileResponse.ok) {
          const profileData = await profileResponse.json();
          if (profileData.success && profileData.user) {
            setProfile({
              height: profileData.user.height || null
            });
          }
        }
      }
    } catch (error) {
      console.error('[BodyMeasurements] Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMeasurements();
  }, [loadMeasurements, refreshKey]);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dayLabels = ["일", "월", "화", "수", "목", "금", "토"];
    const dayLabel = dayLabels[date.getDay()];
    return `${year}년 ${month}월 ${day}일 (${dayLabel})`;
  };

  const handleAddSuccess = () => {
    setShowAddModal(false);
    setRefreshKey(prev => prev + 1);
  };

  const handleProfileUpdate = (updatedProfile: { height?: number | null } | null) => {
    setProfile(updatedProfile);
  };

  const handleSyncSuccess = () => {
    setShowSyncModal(false);
    setRefreshKey(prev => prev + 1);
  };

  const handleDelete = async (id: string | number) => {
    if (!window.confirm('삭제하시겠습니까?')) return;
    try {
      await deleteBodyMeasurement(String(id));
      setSelectedMeasurement(null); // 상세보기에서 리스트로 이동
      setRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error('[BodyMeasurements] Error deleting:', error);
    }
  };

  // 상세뷰
  if (selectedMeasurement) {
    return (
      <SimpleModulePage
        title="신체정보"
        subtitle={formatDate(selectedMeasurement.measurement_time)}
        icon="📏"
        onBack={() => setSelectedMeasurement(null)}
      >
        <SectionCard>
          <div className="mb-3">
            <p className="text-[13px] font-semibold text-slate-900 mb-2">
              {formatDate(selectedMeasurement.measurement_time)}
            </p>
            <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
              <span className="text-[24px]">📏</span>
              <div>
                <p className="text-[13px] font-semibold text-slate-900">
                  {selectedMeasurement.weight} kg
                </p>
                <p className="text-[11px] text-slate-600">
                  {selectedMeasurement.data_source === 'manual' ? '직접입력' : '동기화'}
                </p>
              </div>
            </div>
          </div>

          <div className="mt-4 space-y-3">
            <div className="p-3 border border-slate-200 rounded-lg">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[11px] text-slate-600">체중</p>
                  <p className="text-[13px] font-semibold text-slate-900">{selectedMeasurement.weight} kg</p>
                </div>
                <div>
                  <p className="text-[11px] text-slate-600">BMI</p>
                  <p className="text-[13px] font-semibold text-slate-900">{selectedMeasurement.bmi?.toFixed(1)} kg/m²</p>
                </div>
              </div>
            </div>

            <div className="p-3 border border-slate-200 rounded-lg">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[11px] text-slate-600">체지방</p>
                  <p className="text-[13px] font-semibold text-slate-900">{selectedMeasurement.body_fat_percentage ?? '-'}%</p>
                </div>
                <div>
                  <p className="text-[11px] text-slate-600">근육량</p>
                  <p className="text-[13px] font-semibold text-slate-900">{selectedMeasurement.skeletal_muscle_mass ?? '-'} kg</p>
                </div>
              </div>
            </div>

            <div className="p-3 border border-slate-200 rounded-lg">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[11px] text-slate-600">허리둘레</p>
                  <p className="text-[13px] font-semibold text-slate-900">{selectedMeasurement.waist_circumference ?? '-'} cm</p>
                </div>
                <div>
                  <p className="text-[11px] text-slate-600">엉덩이둘레</p>
                  <p className="text-[13px] font-semibold text-slate-900">{selectedMeasurement.hip_circumference ?? '-'} cm</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 flex gap-2">
            <button
              onClick={() => handleDelete(selectedMeasurement.id)}
              className="flex-1 px-3 py-2 bg-red-50 text-red-600 text-[13px] font-semibold rounded-lg hover:bg-red-100 transition-colors"
            >
              삭제
            </button>
          </div>
        </SectionCard>
      </SimpleModulePage>
    );
  }

  // 목록뷰
  return (
    <SimpleModulePage
      title="신체정보"
      subtitle="체중, BMI, 체지방 등 관리"
      icon="📏"
      onBack={onBack}
    >
      {/* 액션 버튼 (2열 레이아웃) */}
      <div className="mx-4 mb-4 grid grid-cols-2 gap-3">
        <button
          onClick={() => setShowAddModal(true)}
          className="px-4 py-3 bg-blue-500 text-white text-[13px] font-semibold rounded-lg hover:bg-blue-600 transition-colors"
        >
          ✏️ 직접 입력
        </button>
        <button
          onClick={() => setShowSyncModal(true)}
          className="px-4 py-3 bg-violet-500 text-white text-[13px] font-semibold rounded-lg hover:bg-violet-600 transition-colors"
        >
          🔄 디바이스 동기화
        </button>
      </div>

      {/* Dashboard 요약 영역 (데이터가 있을 때만 표시) */}
      {measurements.length > 0 && (
        <SectionCard>
          <div>
            <p className="text-[13px] font-semibold text-slate-900 mb-3">최신 측정</p>
            <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg mb-4">
              <span className="text-[24px]">📏</span>
              <div className="flex-1">
                <p className="text-[13px] font-semibold text-slate-900">
                  {measurements[0].weight} kg
                </p>
                <p className="text-[11px] text-slate-600">
                  {formatDate(measurements[0].measurement_time)}
                </p>
              </div>
              <span className="inline-block px-2 py-1 bg-blue-200 text-blue-700 text-[10px] font-semibold rounded">
                {measurements[0].data_source === 'manual' ? '직접입력' : '동기화'}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="p-2 bg-blue-100 rounded-lg border border-blue-200">
                <p className="text-[10px] text-slate-600">BMI</p>
                <p className="text-[13px] font-semibold text-slate-900 mt-1">{measurements[0].bmi?.toFixed(1)}</p>
              </div>
              <div className="p-2 bg-orange-100 rounded-lg border border-orange-200">
                <p className="text-[10px] text-slate-600">체지방</p>
                <p className="text-[13px] font-semibold text-slate-900 mt-1">{measurements[0].body_fat_percentage ?? '-'}%</p>
              </div>
              <div className="p-2 bg-green-100 rounded-lg border border-green-200">
                <p className="text-[10px] text-slate-600">근육량</p>
                <p className="text-[13px] font-semibold text-slate-900 mt-1">{measurements[0].skeletal_muscle_mass ?? '-'} kg</p>
              </div>
              <div className="p-2 bg-purple-100 rounded-lg border border-purple-200">
                <p className="text-[10px] text-slate-600">허리/엉덩이</p>
                <p className="text-[13px] font-semibold text-slate-900 mt-1">
                  {measurements[0].waist_circumference ?? '-'}
                  {measurements[0].hip_circumference ? `/${measurements[0].hip_circumference}` : ''}
                </p>
              </div>
            </div>
          </div>
        </SectionCard>
      )}

      {/* 측정 기록 목록 */}
      {loading ? (
        <SectionCard>
          <p className="text-[13px] text-slate-600 text-center">로딩 중...</p>
        </SectionCard>
      ) : measurements.length === 0 ? (
        <SectionCard>
          <div className="text-center py-4">
            <p className="text-[13px] text-slate-600">기록된 신체정보가 없습니다</p>
            <p className="text-[11px] text-slate-400 mt-2">직접 입력하거나 디바이스에서 동기화해보세요</p>
          </div>
        </SectionCard>
      ) : (
        <div className="space-y-2">
          {measurements.map((measurement) => (
            <SectionCard
              key={measurement.id}
              className="cursor-pointer hover:bg-slate-50 transition-colors"
            >
              <div
                onClick={() => setSelectedMeasurement(measurement)}
                className="flex items-center justify-between"
              >
                <div>
                  <p className="text-[13px] font-semibold text-slate-900 mb-1">
                    {formatDate(measurement.measurement_time)}
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="inline-block px-2 py-1 bg-blue-100 text-blue-700 text-[10px] font-semibold rounded">
                      {measurement.weight} kg
                    </span>
                    <span className="inline-block px-2 py-1 bg-slate-100 text-slate-700 text-[10px] font-semibold rounded">
                      BMI {measurement.bmi?.toFixed(1)}
                    </span>
                    <span className="inline-block px-2 py-1 bg-slate-100 text-slate-600 text-[10px] rounded">
                      {measurement.data_source === 'manual' ? '직접입력' : measurement.data_source}
                    </span>
                  </div>
                </div>
                <span className="text-slate-400">→</span>
              </div>
            </SectionCard>
          ))}
        </div>
      )}

      {/* 모달들 */}
      <BodyMeasurementModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={handleAddSuccess}
        profile={profile}
        onProfileUpdate={handleProfileUpdate}
        onError={() => setRefreshKey(prev => prev + 1)}
      />

      <BodyMeasurementSyncModal
        isOpen={showSyncModal}
        onClose={() => setShowSyncModal(false)}
        onSuccess={handleSyncSuccess}
        onError={() => setRefreshKey(prev => prev + 1)}
      />
    </SimpleModulePage>
  );
};

export default BodyMeasurementsPage;
